import React from 'react';
import { Card } from '../../components/ui/Card';
import { BarChart, LineChart } from 'lucide-react';
import { DollarSign, Users, Target, TrendingUp, Play, Heart, Share2 } from 'lucide-react';

const performanceData = {
  labels: ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun'],
  datasets: [{
    label: 'In-Feed Ads',
    data: [800, 1200, 1500, 1800, 2200, 2500],
    borderColor: 'rgb(238, 29, 82)',
    backgroundColor: 'rgba(238, 29, 82, 0.1)',
    fill: true,
  }, {
    label: 'Spark Ads',
    data: [600, 900, 1200, 1500, 1800, 2100],
    borderColor: 'rgb(69, 230, 247)',
    backgroundColor: 'rgba(69, 230, 247, 0.1)',
    fill: true,
  }]
};

const audiencePerformance = {
  labels: ['Gen Z', 'Millennials', 'Gen X', 'Others'],
  datasets: [{
    data: [45, 35, 15, 5],
    backgroundColor: [
      'rgb(238, 29, 82)',
      'rgb(69, 230, 247)',
      'rgb(0, 230, 118)',
      'rgb(255, 145, 66)'
    ],
  }]
};

export default function TikTokAds() {
  return (
    <div className="space-y-6 p-6">
      <div className="flex justify-between items-center">
        <h1 className="text-2xl font-semibold">TikTok Ads Dashboard</h1>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        <StatCard
          title="Total Spend"
          value="$18,234"
          change="+25.8%"
          icon={<DollarSign />}
        />
        <StatCard
          title="Views"
          value="5.6M"
          change="+42.3%"
          icon={<Play />}
        />
        <StatCard
          title="Engagement Rate"
          value="4.8%"
          change="+1.2%"
          icon={<Heart />}
        />
        <StatCard
          title="Shares"
          value="12.5K"
          change="+35.7%"
          icon={<Share2 />}
        />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <div className="md:col-span-2">
          <Card className="p-6 rounded-2xl">
            <h2 className="text-lg font-semibold mb-4">Campaign Performance</h2>
            <LineChart data={performanceData} />
          </Card>
        </div>
        <div>
          <Card className="p-6 rounded-2xl">
            <h2 className="text-lg font-semibold mb-4">Audience Demographics</h2>
            <BarChart data={audiencePerformance} />
          </Card>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <Card className="p-6 rounded-2xl">
          <h2 className="text-lg font-semibold mb-4">Top Performing Videos</h2>
          <div className="overflow-x-auto">
            <table className="min-w-full">
              <thead>
                <tr className="border-b">
                  <th className="text-left py-3">Video</th>
                  <th className="text-left py-3">Views</th>
                  <th className="text-left py-3">Engagement</th>
                  <th className="text-left py-3">Conv. Rate</th>
                </tr>
              </thead>
              <tbody>
                <VideoRow
                  name="Summer Collection"
                  views="1.2M"
                  engagement="5.2%"
                  convRate="2.8%"
                />
                <VideoRow
                  name="Product Tutorial"
                  views="890K"
                  engagement="4.8%"
                  convRate="3.1%"
                />
                <VideoRow
                  name="UGC Campaign"
                  views="750K"
                  engagement="6.1%"
                  convRate="2.5%"
                />
              </tbody>
            </table>
          </div>
        </Card>

        <Card className="p-6 rounded-2xl">
          <h2 className="text-lg font-semibold mb-4">Ad Performance</h2>
          <div className="space-y-4">
            <AdType
              title="In-Feed Ads"
              icon={<Play className="w-5 h-5" />}
              stats={{
                views: "2.3M",
                engagement: "4.5%",
                cpm: "$2.80"
              }}
            />
            <AdType
              title="Spark Ads"
              icon={<Heart className="w-5 h-5" />}
              stats={{
                views: "1.8M",
                engagement: "5.2%",
                cpm: "$3.20"
              }}
            />
            <AdType
              title="TopView Ads"
              icon={<Target className="w-5 h-5" />}
              stats={{
                views: "980K",
                engagement: "3.8%",
                cpm: "$4.50"
              }}
            />
          </div>
        </Card>
      </div>
    </div>
  );
}

function StatCard({ title, value, change, icon }) {
  const isPositive = change?.startsWith('+');
  return (
    <Card className="p-6 rounded-2xl">
      <div className="flex items-center gap-4">
        <div className="w-12 h-12 rounded-full bg-pink-50 flex items-center justify-center text-pink-600">
          {icon}
        </div>
        <div>
          <p className="text-sm text-gray-600">{title}</p>
          <div className="flex items-center gap-2">
            <p className="text-2xl font-bold">{value}</p>
            {change && (
              <span className={`text-sm ${isPositive ? 'text-green-500' : 'text-red-500'}`}>
                {change}
              </span>
            )}
          </div>
        </div>
      </div>
    </Card>
  );
}

function VideoRow({ name, views, engagement, convRate }) {
  return (
    <tr className="border-b">
      <td className="py-3">{name}</td>
      <td className="py-3">{views}</td>
      <td className="py-3">{engagement}</td>
      <td className="py-3">{convRate}</td>
    </tr>
  );
}

function AdType({ title, icon, stats }) {
  return (
    <div className="flex items-center justify-between p-4 bg-gray-50 rounded-xl">
      <div className="flex items-center gap-3">
        <div className="w-10 h-10 rounded-full bg-pink-100 flex items-center justify-center text-pink-600">
          {icon}
        </div>
        <span className="font-medium">{title}</span>
      </div>
      <div className="flex gap-6">
        <div>
          <p className="text-sm text-gray-600">Views</p>
          <p className="font-medium">{stats.views}</p>
        </div>
        <div>
          <p className="text-sm text-gray-600">Eng.</p>
          <p className="font-medium">{stats.engagement}</p>
        </div>
        <div>
          <p className="text-sm text-gray-600">CPM</p>
          <p className="font-medium">{stats.cpm}</p>
        </div>
      </div>
    </div>
  );
} 